import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Filter,
  CheckCircle2,
  Clock,
  FileText,
  Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

// Card component for displaying a single record
const RecordCard = ({ record, onClick, formatDate }) => {
  const hasInspection = record.balirna_inspection;
  const kitchenHandover = record.kitchen_handover;
  const isVerified = kitchenHandover?.balirna_verified;
  
  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all ${
        hasInspection ? 'border-green-200 bg-green-50/30' : 
        isVerified ? 'border-blue-200 bg-blue-50/30' : 
        'hover:border-orange-300'
      }`}
      onClick={onClick}
      data-testid={`balirna-record-${record.id}`}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="font-mono text-sm text-slate-500">{record.doklad}</p>
            <h3 className="font-semibold text-slate-800 mt-1 line-clamp-2">
              {record.data?.Popis || record.data?.Výrobek || 'Bez názvu'}
            </h3>
          </div>
          {hasInspection ? (
            <Badge className="bg-green-100 text-green-700 border-green-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Hotovo
            </Badge>
          ) : isVerified ? (
            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Převzato
            </Badge>
          ) : (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              <Clock className="w-3 h-3 mr-1" />
              K převzetí
            </Badge>
          )}
        </div>
        
        <div className="space-y-1 text-sm text-slate-600">
          {record.data?.Šarže && (
            <div className="flex justify-between">
              <span className="text-slate-500">Šarže:</span>
              <span className="font-medium">{record.data.Šarže}</span>
            </div>
          )}
          {record.data?.['Číslo šarže'] && (
            <div className="flex justify-between">
              <span className="text-slate-500">Číslo šarže:</span>
              <span className="font-medium">{record.data['Číslo šarže']}</span>
            </div>
          )}
          {record.data?.Stav && (
            <div className="flex justify-between">
              <span className="text-slate-500">Stav:</span>
              <span className="font-medium">{record.data.Stav}</span>
            </div>
          )}
        </div>
        
        {/* Kitchen handover info */}
        {kitchenHandover && kitchenHandover.items?.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-slate-500 mb-1">Předáno z kuchyně:</p>
            <div className="flex flex-wrap gap-1">
              {kitchenHandover.items.slice(0, 3).map((item, idx) => (
                <span key={idx} className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                  {item.name}: {item.quantity} {item.unit}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <span className="text-xs text-slate-400">
            Plán. datum: {record.data?.['Plán. datum od'] ? new Date(record.data['Plán. datum od']).toLocaleDateString('cs-CZ') : '-'}
          </span>
          <Button variant="ghost" size="sm" className="text-orange-600">
            {hasInspection ? (
              <>
                <FileText className="w-4 h-4 mr-1" />
                Zobrazit
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-1" />
                Vyplnit
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const BalirnaPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeFilters, setActiveFilters] = useState(0);
  const [statusFilter, setStatusFilter] = useState('to_verify'); // default: K převzetí

  const canEdit = hasPagePermission(user, 'balirna', 'edit');
  const isAdmin = hasPagePermission(user, 'admin', 'edit');

  useEffect(() => {
    fetchRecords();
  }, [page, searchTerm]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/balirna/production-records?page=${page}&limit=30&search=${searchTerm}`);
      setRecords(response.data.records);
      setTotalPages(response.data.pages);
      setTotalRecords(response.data.total);
      setActiveFilters(response.data.active_filters || 0);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Chyba při načítání záznamů');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd.MM.yyyy', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  const handleRecordClick = (record) => {
    const isVerified = record.kitchen_handover?.balirna_verified;
    
    if (record.balirna_inspection) {
      // Already has inspection - view/edit it
      navigate(`/balirna/${record.balirna_inspection.id}`);
    } else if (!isVerified) {
      // Not verified yet - go to verification page
      navigate(`/balirna/overit/${record.id}`);
    } else {
      // Verified, can create inspection
      navigate(`/balirna/new/${record.id}`);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Package className="w-7 h-7 text-orange-600" />
              Balírna
            </h1>
            <p className="text-slate-500 mt-1">Kontrolní listy balení</p>
          </div>
          
          {isAdmin && (
            <Link to="/admin/balirna">
              <Button variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                Nastavení filtrů
              </Button>
            </Link>
          )}
        </div>

        {/* Filter info */}
        {activeFilters > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <Filter className="w-4 h-4 text-orange-600" />
            <span className="text-orange-800 text-sm">
              Aktivní filtry: <strong>{activeFilters}</strong> • 
              Zobrazeno {totalRecords} filtrovaných záznamů
            </span>
            {isAdmin && (
              <Link to="/admin/balirna" className="ml-auto text-orange-600 hover:text-orange-700 text-sm underline">
                Upravit filtry
              </Link>
            )}
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Hledat podle čísla dokladu nebo názvu produktu..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="pl-10"
              data-testid="search-balirna"
            />
          </div>
        </div>

        {/* Status Filter Tabs */}
        {(() => {
          const toVerifyCount = records.filter(r => !r.kitchen_handover?.balirna_verified && !r.balirna_inspection).length;
          const verifiedCount = records.filter(r => r.kitchen_handover?.balirna_verified && !r.balirna_inspection).length;
          const completedCount = records.filter(r => r.balirna_inspection).length;
          return (
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setStatusFilter('to_verify')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'to_verify'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white border text-slate-600 hover:bg-orange-50'
                }`}
              >
                <Clock className="w-4 h-4" />
                K převzetí
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  statusFilter === 'to_verify' ? 'bg-orange-400' : 'bg-orange-100 text-orange-700'
                }`}>
                  {toVerifyCount}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter('verified')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'verified'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white border text-slate-600 hover:bg-blue-50'
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                Převzato
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  statusFilter === 'verified' ? 'bg-blue-400' : 'bg-blue-100 text-blue-700'
                }`}>
                  {verifiedCount}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter('completed')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'completed'
                    ? 'bg-green-500 text-white'
                    : 'bg-white border text-slate-600 hover:bg-green-50'
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                Hotovo
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  statusFilter === 'completed' ? 'bg-green-400' : 'bg-green-100 text-green-700'
                }`}>
                  {completedCount}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter('all')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'all'
                    ? 'bg-slate-800 text-white'
                    : 'bg-white border text-slate-600 hover:bg-slate-50'
                }`}
              >
                Vše
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  statusFilter === 'all' ? 'bg-slate-600' : 'bg-slate-200'
                }`}>
                  {records.length}
                </span>
              </button>
            </div>
          );
        })()}

        {/* Records Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {searchTerm ? 'Žádné záznamy nenalezeny' : 'Žádné záznamy k převzetí'}
              </h3>
              <p className="text-slate-400">
                {searchTerm ? 'Zkuste jiný vyhledávací výraz' : 
                  'Kuchyně zatím nepředala žádné výrobky. Záznamy se zde zobrazí až po potvrzení předávky z kuchyně.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Filtrované záznamy */}
            {(() => {
              let filteredRecords = records;
              if (statusFilter === 'to_verify') {
                filteredRecords = records.filter(r => !r.kitchen_handover?.balirna_verified && !r.balirna_inspection);
              } else if (statusFilter === 'verified') {
                filteredRecords = records.filter(r => r.kitchen_handover?.balirna_verified && !r.balirna_inspection);
              } else if (statusFilter === 'completed') {
                filteredRecords = records.filter(r => r.balirna_inspection);
              }
              
              if (filteredRecords.length === 0) {
                return (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-600 mb-2">
                        {statusFilter === 'to_verify' ? 'Žádné záznamy k převzetí' : 
                         statusFilter === 'verified' ? 'Žádné převzaté záznamy čekající na kontrolu' :
                         statusFilter === 'completed' ? 'Žádné dokončené záznamy' : 'Žádné záznamy'}
                      </h3>
                    </CardContent>
                  </Card>
                );
              }
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredRecords.map((record) => (
                    <RecordCard key={record.id} record={record} onClick={() => handleRecordClick(record)} formatDate={formatDate} />
                  ))}
                </div>
              );
            })()}
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <span className="text-sm text-slate-500">
              Stránka {page} z {totalPages} ({totalRecords} záznamů)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BalirnaPage;
